/* eslint-disable import/no-named-as-default-member */
/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable @typescript-eslint/indent */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { effect } from '@preact/signals-react'
import { notification, Modal } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'

import { encryptData } from '@/helpers'
import offlineDB from '@/indexeddb'
import {
  EApplicationStatus,
  TApplication,
  EApplicationType,
  EApplicationCreateMode,
  EFormFieldTypes,
} from '@/interfaces'
import { consultarRPA } from '@/services/rpaService'

import {
  appStateActions,
  formStateActions,
  offlineStateActions,
} from '../actions'
import { ConvocatoriasState } from '../convocatorias'
import { FormState } from '../form'
import { OfflineState } from '../offline-management'
import {
  transFormAppDataToSyncData,
  updateOfflineCurrentApplicationData,
} from '../offline-management/utils'

import applicationRequests from './requests'
import {
  checkViewAccess,
  generateCustomId,
  normalizeSynchedApp,
  setCurrentEntityDataOnSave,
  setFormInitialValues,
} from './utils'

import { ApplicationsState } from '.'
import { MdOutlineWifiProtectedSetup } from 'react-icons/md'
import { setCurrentFormFields } from '../form/utils'

effect(updateOfflineCurrentApplicationData)

export const applicationsStateActions = {
  changeStatus: async (
    newStatus: EApplicationStatus,
    applicationId: string | number
  ) => {
    await applicationRequests.changeApplicationStatus(newStatus, applicationId)

    const isCorrecltyFilledStatus =
      newStatus === EApplicationStatus.CORRECTLY_FILLED

    const currentApplicationsData = ApplicationsState.applications.peek()
    const currentApplicationData = ApplicationsState.currentApplication.peek()

    const enddate = dayjs().format('YYYY-MM-DD')

    if (currentApplicationData?.id === applicationId) {
      ApplicationsState.currentApplication.value = {
        ...currentApplicationData,
        enddate: isCorrecltyFilledStatus
          ? enddate
          : currentApplicationData.enddate,
        estadoName: newStatus,
      }
    }

    ApplicationsState.applications.value = currentApplicationsData.map(
      (item) => {
        if (item.id === applicationId) {
          item.estadoName = newStatus
          item.enddate = isCorrecltyFilledStatus ? enddate : item.enddate
        }

        return item
      }
    )
  },
  cleanFilters: () => {
    ApplicationsState.filters.value = {
      status: null,
      text: null,
      type: null,
    }
  },
  closeImportModal: () => {
    ApplicationsState.importApplicationType.value = null
    ApplicationsState.openImportModal.value = false
  },
  createNewApplication: async (applicationType: EApplicationType) => {
    const canViewApp = checkViewAccess()

    if (!canViewApp) {
      notification.warning({
        description:
          'Lo sentimos, no cuentas con los permisos suficientes para crear una solicitud',
        message: 'Revisa tus permisos',
        placement: 'top',
      })

      return
    }

    ApplicationsState.currentApplicationType.value = applicationType

    const isOnline = OfflineState.isOnline.peek()

    let newApplicationId = ''
    let newApplicationConsecutivoHogar: string | number = 'X'

    if (isOnline) {
      const [response] = await Promise.all([
        applicationRequests.createNewApplication(),
        formStateActions.getCurrentForm(),
      ])

      newApplicationId = response.data.id
      newApplicationConsecutivoHogar = response.data.consecutivoHogar
    } else {
      newApplicationId = generateCustomId()
    }

    if (!newApplicationId) {
      throw Error('Error')
    }

    if (applicationType) {
      setCurrentFormFields(applicationType)
    }

    applicationsStateActions.setNewApplicationValues(
      newApplicationId,
      newApplicationConsecutivoHogar
    )
    formStateActions.setCurrentEntity()

    applicationsStateActions.setApplicationToEditMode()
    formStateActions.showForm()
    appStateActions.hideTopbar()
  },
  createRowData: async (rowData: TApplication) => {
    const currentEntityDateFields = FormState.currentEntityDateFields.peek()

    if (currentEntityDateFields.length > 0) {
      currentEntityDateFields.forEach((field) => {
        const fieldValue = (rowData[field.name] as Dayjs) ?? undefined

        if (field.type === EFormFieldTypes.DATE) {
          rowData[field.name] = fieldValue.format('YYYY-MM-DD')
        }

        if (field.type === EFormFieldTypes.DATE_TIME) {
          rowData[field.name] = fieldValue.format('YYYY-MM-DD HH:mm:ss')
        }
      })
    }

    const isOnline = OfflineState.isOnline.peek()

    if (isOnline) {
      const response = await applicationRequests.createRowData(rowData)
      rowData.id = response.data.id
    } else {
      rowData.id = generateCustomId()
    }

    ApplicationsState.currentEntityTableData.value = [
      rowData,
      ...ApplicationsState.currentEntityTableData.peek(),
    ]

    applicationsStateActions.updateApplicationLocalRowData()
  },
  deleteApplication: async (applicationId: string) => {
    const isOnline = OfflineState.isOnline.peek()

    if (isOnline) {
      await applicationRequests.deleteApplication(applicationId)
    }

    if (FormState.showForm.value) {
      await applicationsStateActions.goToHome()
    }

    ApplicationsState.applications.value = ApplicationsState.applications
      .peek()
      .filter((item) => item.id !== applicationId)

    await offlineStateActions.deleteApplication(applicationId)
  },
  deleteRowdata: async (rowDataId: string | number) => {
    const isOnline = OfflineState.isOnline.value

    if (isOnline) {
      await applicationRequests.deleteRowData(rowDataId)
    }

    const currentTableData = ApplicationsState.currentEntityTableData
      .peek()
      .filter((item) => item.id !== rowDataId)
    ApplicationsState.currentEntityTableData.value = [...currentTableData]

    applicationsStateActions.updateApplicationLocalRowData()
  },
  editApplication: async (applicationId: string) => {
    const canViewApp = checkViewAccess()

    if (!canViewApp) {
      notification.warning({
        description:
          'Lo sentimos, no cuentas con los permisos suficientes para editar las solicitudes',
        message: 'Revisa tus permisos',
        placement: 'top',
      })

      return false
    }

    applicationsStateActions.setApplicationToEditMode()

    if (!FormState.showForm.value) {
      await applicationsStateActions.openApplicationView(applicationId)
    }

    return !FormState.showForm.value
  },
  exportData: async () => {
    ApplicationsState.loading.value = {
      ...ApplicationsState.loading.peek(),
      export: true,
    }

    try {
      const convocatoriaId =
        ConvocatoriasState.currentConvocatoria.peek().id ?? '67ad5e8c146b36d27'

      if (convocatoriaId) {
        const response = await applicationRequests.exportData(convocatoriaId)

        if (response) {
          const blob = new Blob([response], {
            type: 'text/csv;charset=utf-8;',
          })

          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          const fileName = `InfoSolicitudes_${dayjs().format('DDMMYYYYHHmmss')}.csv`

          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        }
      }
    } catch (error) {
      notification.error({
        message: 'Error exportando la información',
        description: 'Por favor intenta de nuevo o comunícate con nosotros.',
      })
      console.error(error)
    } finally {
      ApplicationsState.loading.value = {
        ...ApplicationsState.loading.peek(),
        export: false,
      }
    }
  },

  getAllApplications: async () => {
    ApplicationsState.loading.value = {
      ...ApplicationsState.loading.peek(),
      getAll: true,
    }

    const isOnline = OfflineState.isOnline.peek()

    let applicationsItems: TApplication[] =
      ApplicationsState.applications.peek()

    if (isOnline) {
      const [applicationData, applicationOffline] = await Promise.all([
        applicationRequests.getAllApplications().finally(() => {
          ApplicationsState.loading.value = {
            ...ApplicationsState.loading.peek(),
            getAll: false,
          }
        }),
        offlineDB.getAllFromStore(offlineDB.stores.APPLICATIONS_STORE),
      ])

      applicationsItems = [
        ...applicationOffline.filter(
          (item) => item.createMode === EApplicationCreateMode.OFFLINE
        ),
        ...applicationData.data,
      ]
    }

    ApplicationsState.applications.value = [...applicationsItems]
    ApplicationsState.loading.value = {
      ...ApplicationsState.loading.peek(),
      getAll: false,
    }
  },
  getAllApplicationsOffline: async () => {
    ApplicationsState.loading.value = {
      ...ApplicationsState.loading.peek(),
      getAll: true,
    }

    const applicationsOffline = await offlineDB.getAllFromStore(
      offlineDB.stores.APPLICATIONS_STORE
    )

    ApplicationsState.applications.value = [...applicationsOffline]
    offlineStateActions.setOfflineStoreApplications(
      applicationsOffline.map((item) => item.id as string)
    )

    ApplicationsState.loading.value = {
      ...ApplicationsState.loading.peek(),
      getAll: false,
    }
  },
  getApplicationById: async (applicationId: string) => {
    ApplicationsState.loading.value = {
      ...ApplicationsState.loading.peek(),
      getById: applicationId,
    }

    let applicationData: TApplication = {}

    const isOnline = OfflineState.isOnline.peek()

    if (isOnline) {
      const applicationResponse = await applicationRequests
        .getApplicationData(applicationId)
        .finally(() => {
          ApplicationsState.loading.value = {
            ...ApplicationsState.loading.peek(),
            getById: false,
          }
        })

      applicationData = applicationResponse.data
    } else {
      applicationData = await offlineDB.getFromStore(
        offlineDB.stores.APPLICATIONS_STORE,
        applicationId
      )
      ApplicationsState.loading.value = {
        ...ApplicationsState.loading.peek(),
        getById: false,
      }
    }

    ApplicationsState.currentApplication.value = {
      ...applicationData,
    }
  },
  getApplicationStatus: async (applicationId?: string) => {
    const currentApplicationData = ApplicationsState.currentApplication.peek()
    const currentApplicationId = applicationId ?? currentApplicationData?.id

    if (currentApplicationId) {
      const statusData = await applicationRequests.getApplicationStatus(
        currentApplicationId as string
      )

      const statusName =
        statusData.data.estadoName ?? currentApplicationData?.estadoName

      if (statusName && currentApplicationData) {
        ApplicationsState.currentApplication.value = {
          ...currentApplicationData,
          estadoName: statusName,
        }
      }
    }
  },
  goToHome: async () => {
    const currentApplicationValues = ApplicationsState.currentApplication.peek()
    let currentApplications = ApplicationsState.applications.peek()

    if (currentApplicationValues) {
      const applicationExist = currentApplications.findIndex(
        (item) => item.id === currentApplicationValues.id
      )

      if (applicationExist >= 0) {
        currentApplications[applicationExist] = { ...currentApplicationValues }
      } else {
        currentApplications = [currentApplicationValues, ...currentApplications]
      }

      ApplicationsState.applications.value = [...currentApplications]
    }

    formStateActions.cleanFormState()
    appStateActions.showTopbar()

    ApplicationsState.currentApplication.value = null
    ApplicationsState.currentEntityTableData.value = []
    ApplicationsState.viewMode.value = {
      edit: false,
      view: false,
    }

    FormState.hasRequiredFieldsEmpty.value = false

    await offlineDB.deleteFromStore({
      key: offlineDB.userStoreIds.application,
      storeName: offlineDB.stores.USER_STORE,
    })
  },
  importData: async (archivoBase64: string) => {
    ApplicationsState.loading.value = {
      ...ApplicationsState.loading.peek(),
      import: true,
    }

    try {
      const convocatoriaId = ConvocatoriasState.currentConvocatoria.peek().id
      const teamId = ConvocatoriasState.currentCity.peek()
      const type = ApplicationsState.importApplicationType.peek()

      if (!(convocatoriaId && teamId && type)) {
        notification.warning({
          message:
            'Debes seleccionar una convocatoria, un municipio y un tipo de solicitud para continuar',
          description: 'Por favor intenta de nuevo o comunícate con nosotros.',
        })
        return
      }

      await applicationRequests.importData(
        convocatoriaId,
        teamId,
        archivoBase64,
        type
      )

      notification.success({
        message: 'Información de solicitud importada exitosamente',
      })
    } catch (error) {
      notification.error({
        message: 'Error importando la información',
        description: 'Por favor intenta de nuevo o comunícate con nosotros.',
      })
      console.error(error)
    } finally {
      ApplicationsState.loading.value = {
        ...ApplicationsState.loading.peek(),
        import: false,
      }
    }
  },
  initReview: async (applicationId: string) => {
    await applicationsStateActions.changeStatus(
      EApplicationStatus.IN_REVIEW,
      applicationId
    )
    await applicationsStateActions.viewApplication(applicationId)
  },
  openApplicationView: async (applicationId: string) => {
    ApplicationsState.loading.value = {
      ...ApplicationsState.loading.peek(),
      getById: applicationId,
    }

    const isOnline = OfflineState.isOnline.value
    let applicationData: TApplication = {}

    if (isOnline) {
      const [applicationDataResponse] = await Promise.all([
        applicationRequests.getApplicationData(applicationId).finally(() => {
          ApplicationsState.loading.value = {
            ...ApplicationsState.loading.peek(),
            getById: false,
          }
        }),
        formStateActions.getCurrentForm(),
      ])

      applicationData = applicationDataResponse.data
    } else {
      applicationData = await offlineDB.getFromStore(
        offlineDB.stores.APPLICATIONS_STORE,
        applicationId
      )
      ApplicationsState.loading.value = {
        ...ApplicationsState.loading.peek(),
        getById: false,
      }
    }

    ApplicationsState.currentApplicationType.value =
      (applicationData.typeimprovement as EApplicationType) ?? null

    if (applicationData.typeimprovement) {
      setCurrentFormFields(applicationData.typeimprovement as EApplicationType)
    }

    applicationsStateActions.setExistingApplicationValues({
      ...applicationData,
    })

    formStateActions.setCurrentEntity()
    formStateActions.showForm()
    appStateActions.hideTopbar()

    ApplicationsState.loading.value = {
      ...ApplicationsState.loading.peek(),
      changeStatus: {},
    }

    await Promise.all([
      offlineDB.saveMultipleToStore(offlineDB.stores.USER_STORE, [
        {
          data: encryptData(applicationId),
          id: offlineDB.userStoreIds.application,
        },
      ]),
      offlineDB.saveMultipleToStore(offlineDB.stores.APPLICATIONS_STORE, [
        applicationData,
      ]),
    ])
  },
  openImportModal: () => {
    const convocatoriaAppTypes =
      ConvocatoriasState.currentConvocatoria.peek().appType ?? []

    if (!convocatoriaAppTypes.length) return

    let currentApplicationType = null

    if (convocatoriaAppTypes.length === 1) {
      currentApplicationType = convocatoriaAppTypes[0]
    }

    ApplicationsState.importApplicationType.value = currentApplicationType
    ApplicationsState.openImportModal.value = true
  },

  saveApplicationData: async (formValues: TApplication) => {
    const isOnline = OfflineState.isOnline.peek()
    // Recuperar el id si falta
    let id = formValues.id || ApplicationsState.currentApplication.peek()?.id
    if (!id) {
      notification.error({
        message: 'No se encontró un ID válido para la postulación',
        description: 'No se puede guardar sin un identificador único. Verifique la carga del formulario.',
        placement: 'top',
      })
      return
    }
    // Construir el objeto de guardado con el id correcto
    const currentEntityFiles = ApplicationsState.currentEntityFilesData.peek()
    const newEntityData: TApplication = { ...formValues, ...currentEntityFiles, id }
    if (isOnline) {
      await applicationRequests.saveApplicationData(newEntityData)
      await applicationsStateActions.getApplicationStatus()
    }
    setCurrentEntityDataOnSave(newEntityData)
  },

  validateAndSaveWithRPA: async (formValues: TApplication) => {
    try {
      console.log('[RPA DEBUG] validateAndSaveWithRPA iniciado con:', formValues)

      // Extraer cédulas para consulta RPA
      const cedulas: string[] = []

      // Cédula principal del documento
      if (formValues?.documento && typeof formValues.documento === 'string') {
        cedulas.push(formValues.documento.trim())
      }

      // Cédula del postulante
      const postulante = formValues?.CPostulante
      if (
        postulante &&
        typeof postulante === 'object' &&
        'documento' in postulante &&
        typeof postulante.documento === 'string'
      ) {
        cedulas.push(postulante.documento.trim())
      }

      // Cédulas del núcleo familiar
      if (Array.isArray(formValues?.CNucleoFamiliar)) {
        formValues.CNucleoFamiliar.forEach((member: any) => {
          if (member?.docNumber && typeof member.docNumber === 'string') {
            cedulas.push(member.docNumber.trim())
          }
        })
      }

      const uniqueCedulas = Array.from(new Set(cedulas.filter(c => c.length > 0)))
      console.log('[RPA DEBUG] validateAndSaveWithRPA - cédulas a consultar:', uniqueCedulas)

      // Usar el id que viene del backend, nunca generar uno nuevo si ya existe
      const id = formValues.id;
      if (!id) {
        throw new Error('No se encontró un ID válido para la postulación. El ID debe venir del backend.');
      }
      let newEntityData: TApplication = { ...formValues, id }

      if (uniqueCedulas.length > 0) {
        try {
          console.log('[RPA DEBUG] Iniciando consulta RPA para cédulas:', uniqueCedulas)

          // Consultar RPA
          const rpaResult = await consultarRPA(uniqueCedulas)
          console.log('[RPA DEBUG] validateAndSaveWithRPA - resultado RPA:', rpaResult)

          // --- VERIFICACIÓN ROBUSTA DE STATUS FAILED ---
          let parsedDetalle: any = rpaResult?.detalle;
          if (typeof rpaResult?.detalle === 'string') {
            try {
              parsedDetalle = JSON.parse(rpaResult.detalle);
            } catch (e) {
              parsedDetalle = {};
            }
          }
          let rpaStatus = parsedDetalle?.status;
          if (rpaStatus === 'FAILED') {
            // Extraer mensaje y errores de sistemas consultados
            let errorMsg = parsedDetalle?.message || 'Error en la consulta RPA.';
            let systems = parsedDetalle?.results || [];
            let systemErrors = systems.map((res: any) => {
              let sysErrs = Object.entries(res?.results || {}).map(([sys, val]: [string, any]) => `${sys}: ${val?.error || val?.status}`);
              return `${res.cedula}: ${sysErrs.join('; ')}`;
            }).join(' | ');
            Modal.error({
              title: 'Error en Validación RPA',
              content: `${errorMsg}${systemErrors ? ' - Detalle: ' + systemErrors : ''}`,
              okText: 'Cerrar',
              centered: true,
              maskClosable: false,
            });
            // Detener el flujo, NO guardar nada
            return;
          }

          // --- NUEVA LÓGICA INDIVIDUAL ---
          let inhabilitado = false;
          let detalleValidacion = rpaResult.detalle;
          let sistemasEncontrados = rpaResult.sistemas;
          let isIndividual = false;
          if (typeof detalleValidacion === 'string' && detalleValidacion.trim().startsWith('{')) {
            try {
              parsedDetalle = JSON.parse(detalleValidacion);
              // Si es consulta individual (solo una cédula en results)
              if (parsedDetalle?.results && parsedDetalle.results.length === 1) {
                isIndividual = true;
                const systems = parsedDetalle.results[0]?.results || {};
                // Revisar si algún sistema tiene data con items o tiene_inmuebles true
                let foundProps = false;
                let foundSystems: string[] = [];
                Object.entries(systems).forEach(([system, sysResult]: [string, any]) => {
                  if (sysResult?.status === 'success') {
                    if (Array.isArray(sysResult.data) && sysResult.data.length > 0) {
                      foundProps = true;
                      foundSystems.push(system);
                    }
                    if (system === 'mivivienda' && sysResult.tiene_inmuebles === true) {
                      foundProps = true;
                      foundSystems.push(system);
                    }
                  }
                });
                inhabilitado = foundProps;
                sistemasEncontrados = foundSystems;
                // --- AJUSTE: Si la cédula tiene propiedades, mostrar modal y NO guardar ---
                if (inhabilitado) {
                  // Extraer mensaje y errores de sistemas consultados
                  let errorMsg = parsedDetalle?.message || 'Error en la consulta RPA.';
                  let systems = parsedDetalle?.results || [];
                  let systemErrors = systems.map((res: any) => {
                    let sysErrs = Object.entries(res?.results || {}).map(([sys, val]: [string, any]) => `${sys}: ${val?.error || val?.status}`);
                    return `${res.cedula}: ${sysErrs.join('; ')}`;
                  }).join(' | ');

                  Modal.error({
                    title: 'Error en Validación RPA',
                    content: `${errorMsg}${systemErrors ? ' - Detalle: ' + systemErrors : ''}`,
                    okText: 'Aceptar',
                    centered: true,
                    maskClosable: false,
                    onOk: async () => {
                      const applicationId = ApplicationsState.currentApplication.value?.id;
                      console.debug('[DEBUG][deleteApplication] Modal inhabilitado, id:', applicationId);
                      if (applicationId) {
                        try {
                          await applicationsStateActions.deleteApplication(String(applicationId));
                          console.debug('[DEBUG][deleteApplication] Eliminación completada');
                        } catch (error) {
                          console.error('[ERROR][deleteApplication] Falló la eliminación:', error);
                        }
                      }
                      await applicationsStateActions.goToHome();
                    },
                  });
                  ApplicationsState.currentApplication.value = {
                    ...ApplicationsState.currentApplication.peek(),
                    ...formValues,
                    inhabilitado: true,
                    detalleInhabilitacion: detalleValidacion,
                    ValidacionRpa: 'SI',
                  };
                  applicationsStateActions.showModalInhabilitacion(
                    detalleValidacion,
                    uniqueCedulas.join(', ')
                  );
                  // Detener el flujo, NO guardar nada
                  return;
                }
              }
            } catch (e) {
              // Si no se puede parsear, usar la lógica original
              parsedDetalle = null;
            }
          }
          // --- FIN NUEVA LÓGICA ---
          newEntityData = {
            ...formValues,
            id,
            inhabilitado,
            detalleInhabilitacion: detalleValidacion,
            ValidacionRpa: inhabilitado ? 'SI' : 'NO',
            applicationstatus: inhabilitado
              ? EApplicationStatus.DISABLED
              : formValues.applicationstatus || EApplicationStatus.WITHOUT_START,
            estadoName: inhabilitado
              ? EApplicationStatus.DISABLED
              : formValues.estadoName || EApplicationStatus.WITHOUT_START,
          };
          // Notificación y modal según resultado
          if (!inhabilitado) {
            notification.success({
              message: 'Validación RPA Exitosa',
              description: 'La postulación ha sido validada exitosamente. No se encontraron propiedades registradas.',
              placement: 'top',
              duration: 5,
            });
            // Solo guardar si es elegible
            await applicationsStateActions.saveApplicationDataDirect(newEntityData);
            ApplicationsState.currentApplication.value = {
              ...ApplicationsState.currentApplication.peek(),
              ...newEntityData,
            };
          }
        } catch (rpaError) {
          console.error('[RPA DEBUG] Error durante consulta RPA:', rpaError)
          let detalleError = 'Error técnico desconocido'
          if (rpaError && typeof rpaError === 'object' && 'message' in rpaError) {
            detalleError = `Error técnico: ${(rpaError as any).message}`
          }
          newEntityData = {
            ...formValues,
            id,
            inhabilitado: false,
            detalleInhabilitacion: detalleError,
            ValidacionRpa: 'ERROR',
            applicationstatus: formValues.applicationstatus || EApplicationStatus.WITHOUT_START,
            estadoName: formValues.estadoName || EApplicationStatus.WITHOUT_START,
          }
          notification.warning({
            message: 'Error Técnico en RPA',
            description: 'Ocurrió un error técnico durante la validación. Los datos se guardarán sin validación RPA.',
            placement: 'top',
            duration: 8,
          })
        }

      } else {
        // Si no hay cédulas, guardar normalmente sin validación RPA
        console.log('[RPA DEBUG] validateAndSaveWithRPA - sin cédulas para RPA')
        newEntityData = {
          ...formValues,
          id,
          inhabilitado: false,
          detalleInhabilitacion: '',
          ValidacionRpa: 'NO_CEDULAS',
          applicationstatus: formValues.applicationstatus || EApplicationStatus.WITHOUT_START,
          estadoName: formValues.estadoName || EApplicationStatus.WITHOUT_START,
        }
      }

      // *** GUARDAR SIEMPRE CON FORZAR PERSISTENCIA ***
      console.log('[RPA DEBUG] Guardando postulación (inhabilitada o no):', newEntityData.inhabilitado)

      try {
        // *** USAR saveApplicationDataDirect para saltarse validaciones adicionales ***
        await applicationsStateActions.saveApplicationDataDirect(newEntityData)
        console.log('[RPA DEBUG] validateAndSaveWithRPA - guardado exitoso')

        // *** ACTUALIZAR ESTADO LOCAL DESPUÉS DEL GUARDADO ***
        ApplicationsState.currentApplication.value = {
          ...ApplicationsState.currentApplication.peek(),
          ...newEntityData
        }

      } catch (saveError) {
        console.error('[RPA DEBUG] Error guardando postulación:', saveError)
        let mensaje = 'Error guardando la postulación.'
        if (saveError && typeof saveError === 'object' && 'message' in saveError) {
          mensaje = `Error guardando la postulación: ${(saveError as any).message}`
        }
        throw new Error(mensaje)
      }

      return newEntityData

    } catch (error) {
      console.error('[RPA DEBUG] Error general en validateAndSaveWithRPA:', error)

      notification.error({
        message: 'Error en Validación',
        description: 'Ocurrió un error durante el proceso de validación y guardado.',
        placement: 'top',
      })

      throw error
    }
  },

  // NUEVA: Mostrar modal de inhabilitación
  showModalInhabilitacion: (detalle: string, cedula?: string) => {
    ApplicationsState.modalInhabilitacion.value = {
      visible: true,
      detalle,
      cedula,
    }
  },

  // NUEVA: Cerrar modal de inhabilitación
  closeModalInhabilitacion: () => {
    ApplicationsState.modalInhabilitacion.value = {
      visible: false,
      detalle: '',
      cedula: undefined,
    }
  },

  // *** NUEVA FUNCIÓN PARA GUARDADO DIRECTO ***
  saveApplicationDataDirect: async (formValues: TApplication) => {
    const isOnline = OfflineState.isOnline.peek()
    const currentEntityFiles = ApplicationsState.currentEntityFilesData.peek()
    const newEntityData: TApplication = { ...formValues, ...currentEntityFiles }

    console.log('[RPA DEBUG] saveApplicationDataDirect - datos a guardar:', {
      id: newEntityData.id,
      inhabilitado: newEntityData.inhabilitado,
      applicationstatus: newEntityData.applicationstatus,
      estadoName: newEntityData.estadoName,
      ValidacionRpa: newEntityData.ValidacionRpa
    })

    if (isOnline) {
      await applicationRequests.saveApplicationData(newEntityData)
      await applicationsStateActions.getApplicationStatus()
    }

    setCurrentEntityDataOnSave(newEntityData)

    console.log('[RPA DEBUG] saveApplicationDataDirect - guardado completado')
  },

  setApplicationStatusFilter: (status?: EApplicationStatus) => {
    ApplicationsState.filters.value = {
      ...ApplicationsState.filters.peek(),
      status: status ?? null,
    }
  },
  setApplicationTextFilter: (text?: string) => {
    ApplicationsState.filters.value = {
      ...ApplicationsState.filters.peek(),
      text: text?.trim() ?? null,
    }
  },
  setApplicationToEditMode: () => {
    ApplicationsState.viewMode.value = { edit: true, view: false }
  },
  setApplicationToViewMode: () => {
    ApplicationsState.viewMode.value = { edit: false, view: true }
  },
  setApplicationTypeFilter: (type?: EApplicationType) => {
    ApplicationsState.filters.value = {
      ...ApplicationsState.filters.peek(),
      type: type ?? null,
    }
  },
  setApplicationsSynched: async (applications: TApplication[]) => {
    const applicationsIds = applications.map((item) => item.id as string)
    const appsSynched = applications.map(normalizeSynchedApp)

    await applicationsStateActions.setCurrentApplicationSynched(
      appsSynched,
      applicationsIds
    )

    let currentApplications = [...ApplicationsState.applications.peek()]

    const applicationIdsToDelete: string[] = []

    currentApplications = currentApplications.map((item) => {
      if (applicationsIds.includes(item.id as string)) {
        const appNormalizedIdx = appsSynched.findIndex(
          (appSynched) => item.id === appSynched.id
        )

        if (appNormalizedIdx >= 0) {
          const appNormalized = appsSynched[appNormalizedIdx]

          if (
            appNormalized?.newId &&
            appNormalized.newId !== appNormalized.id
          ) {
            applicationIdsToDelete.push(appNormalized.id as string)

            appNormalized.id = appNormalized.newId

            offlineStateActions.setOfflineStoreApplications([
              appNormalized.id as string,
            ])
          }

          delete appNormalized.newId

          appsSynched[appNormalizedIdx] = { ...appNormalized }

          return { ...appNormalized }
        }
      }

      delete item.newId

      return { ...item }
    })

    await offlineDB.deleteFromStore({
      keys: applicationIdsToDelete,
      storeName: offlineDB.stores.APPLICATIONS_STORE,
    })

    await offlineDB.saveMultipleToStore(
      offlineDB.stores.APPLICATIONS_STORE,
      appsSynched
    )

    ApplicationsState.applications.value = [...currentApplications]
  },
  setCurrentApplicationSynched: async (
    appsSynched: TApplication[],
    applicationsIds: string[]
  ) => {
    const currentApplication = ApplicationsState.currentApplication.peek()
    const currentApplicationIsSynched = Boolean(
      FormState.showForm.peek() &&
        currentApplication?.id &&
        applicationsIds.includes(currentApplication.id as string)
    )

    if (currentApplicationIsSynched) {
      const appNormalized = appsSynched.find(
        (appSynched) => currentApplication?.id === appSynched.id
      )

      let currentApplicationId: string | null = null

      if (appNormalized) {
        currentApplicationId = (appNormalized.newId ??
          appNormalized.id) as string
      }

      if (currentApplicationId) {
        notification.info({
          icon: <MdOutlineWifiProtectedSetup />,
          message: 'Obteniendo información de la solicitud actual',
          placement: 'bottomRight',
        })
        await applicationsStateActions
          .getApplicationById(currentApplicationId)
          .then(() => {
            notification.info({
              description:
                'La información de la solicitud actual se ha sincronizado correctamente',
              icon: <MdOutlineWifiProtectedSetup />,
              message: 'Proceso finalizado',
              placement: 'bottomRight',
            })
          })
          .catch(async () => {
            await applicationsStateActions.goToHome()
            notification.warning({
              description: `Hubo un problema intentando obtener la información nueva asociada a la solicitud actual: ID - ${currentApplicationId} `,
              message: 'Inconsistencia en la información de la solicitud',
              placement: 'bottomRight',
            })
          })
      }
    }
  },
  setCurrentEntityFile: (file: string, fileName: string) => {
    const currentEntityName = FormState.currentEntity.peek()?.name
    const currentApplicationValues = ApplicationsState.currentApplication.peek()

    if (currentEntityName && currentApplicationValues && fileName) {
      const currentEntityValues = currentApplicationValues[
        currentEntityName
      ] as TApplication

      if (currentEntityValues) {
        ApplicationsState.currentApplication.value = {
          ...currentApplicationValues,
          [currentEntityName]: {
            ...currentEntityValues,
            [fileName]: file,
          },
        }
      }

      const currentFilesData = {
        ...ApplicationsState.currentEntityFilesData.peek(),
        [fileName]: file,
      }

      ApplicationsState.currentEntityFilesData.value = { ...currentFilesData }

      FormState.currentFormValues.value = {
        ...FormState.currentFormValues.peek(),
        ...currentFilesData,
      }
    }
  },
  setExistingApplicationValues: (applicationValues: TApplication) => {
    const existingApplicationValues = setFormInitialValues(
      FormState.currentForm.peek(),
      applicationValues
    )

    ApplicationsState.currentApplication.value = {
      ...applicationValues,
      ...existingApplicationValues,
    }
  },
  setImportApplicationType: (applicationType?: EApplicationType) => {
    const currentSelection = ApplicationsState.importApplicationType.peek()

    const isSame = currentSelection === applicationType
    ApplicationsState.importApplicationType.value = isSame
      ? null
      : (applicationType ?? null)
  },
  setNewApplicationValues: (
    applicationId: string | number,
    consecutivoHogar: number | string
  ) => {
    if (!ApplicationsState.currentApplication.peek()) {
      const applicationValues = setFormInitialValues(
        FormState.currentForm.peek()
      )

      const isOnline = OfflineState.isOnline.peek()

      const currentTeamId = ConvocatoriasState.currentCity.peek() as string
      const currentCity =
        ConvocatoriasState.cities
          .peek()
          .find((item) => item.value === currentTeamId)
          ?.label.toUpperCase() ?? ''

      const currentConvocatoria = ConvocatoriasState.currentConvocatoria.peek()

      const newApplicationValues = {
        addressCity: currentCity,
        teamsIds: [currentTeamId],
        teamId: currentTeamId,
        applicationstatus: isOnline
          ? EApplicationStatus.WITHOUT_START
          : EApplicationStatus.OFFLINE,
        consecutivoHogar,
        convocatoriaId: currentConvocatoria.id,
        convocatoriaName: currentConvocatoria.name,
        createMode: isOnline
          ? EApplicationCreateMode.ONLINE
          : EApplicationCreateMode.OFFLINE,
        estadoName: isOnline
          ? EApplicationStatus.WITHOUT_START
          : EApplicationStatus.OFFLINE,
        id: applicationId,
        toSync: !isOnline,
        typeimprovement: ApplicationsState.currentApplicationType.peek(),
        ...applicationValues,
      }

      ApplicationsState.currentApplication.value = newApplicationValues
    }
  },
  syncApplications: async (applications: TApplication[]) => {
    const appsTransformed = applications.map((item) =>
      transFormAppDataToSyncData(item)
    )

    const syncResponse =
      await applicationRequests.syncApplications(appsTransformed)

    const newResponseIds = syncResponse.data

    const appSynched: TApplication[] = []

    applications.forEach((itm: TApplication, idx) => {
      const appSynchedId = newResponseIds[idx]

      if (appSynchedId) {
        itm.newId = appSynchedId
        appSynched.push({ ...itm })
      }
    })

    await applicationsStateActions.setApplicationsSynched(appSynched)
  },
  syncDeletedApplications: async (applications: TApplication[]) => {
    const applicationsIds = applications.map((item) => item.id as string)

    await applicationRequests.syncDeletedApplications(applicationsIds)

    await offlineDB.deleteFromStore({
      keys: applicationsIds,
      storeName: offlineDB.stores.DELETED_APPLIES_STORE,
    })
  },
  toggleShowFilters: () => {
    ApplicationsState.showFilters.value = !ApplicationsState.showFilters.peek()
  },
  toggleImportModal: () => {
    ApplicationsState.openImportModal.value =
      !ApplicationsState.openImportModal.peek()
  },
  updateApplicationLocalRowData: () => {
    const currentEntityName = FormState.currentEntity.peek()?.name
    const currentApplicationValues = ApplicationsState.currentApplication.peek()
    const isOnline = OfflineState.isOnline.peek()

    if (currentEntityName && currentApplicationValues) {
      ApplicationsState.currentApplication.value = {
        ...currentApplicationValues,
        [currentEntityName]: ApplicationsState.currentEntityTableData.value,
        toSync: !isOnline,
      }
    }

    formStateActions.closeSubsectionForm()
  },
  updateRowData: async (rowData: TApplication) => {
    // Validar cédula con RPA antes de guardar
    let cedula =
      rowData.documento ||
      rowData.cedula ||
      rowData.Cedula ||
      rowData.CEDULA
    if (!cedula) {
      for (const key of Object.keys(rowData)) {
        if (
          key.toLowerCase().includes('cedula') ||
          key.toLowerCase().includes('documento')
        ) {
          cedula = rowData[key]
          break
        }
      }
    }
    if (cedula) {
      const rpaResult = await consultarRPA([String(cedula)])
      if (rpaResult.inhabilitado) {
        Modal.error({
          title: 'Miembro con propiedad encontrada',
          content: `No se puede continuar con la postulación porque el miembro (${cedula}) tiene propiedad registrada en sistemas externos.\nLa postulación quedará inhabilitada.`,
        })
        // Cambiar el estado de la postulación a "inhabilitado"
        if (ApplicationsState.currentApplication.value?.id) {
          await applicationsStateActions.changeStatus(EApplicationStatus.DISABLED, String(ApplicationsState.currentApplication.value.id))
        }
        return
      }
    }

    const rowDataId = FormState.entityForm.peek().values.id
    const isOnline = OfflineState.isOnline.peek()

    const currentEntityDateFields = FormState.currentEntityDateFields.peek()

    if (currentEntityDateFields.length > 0) {
      currentEntityDateFields.forEach((field) => {
        const fieldValue = (rowData[field.name] as Dayjs) ?? undefined

        if (field.type === EFormFieldTypes.DATE) {
          rowData[field.name] = fieldValue.format('YYYY-MM-DD')
        }

        if (field.type === EFormFieldTypes.DATE_TIME) {
          rowData[field.name] = fieldValue.format('YYYY-MM-DD HH:mm:ss')
        }
      })
    }

    if (isOnline) {
      await applicationRequests.updateRowData({
        ...rowData,
        id: rowDataId,
      })
    }

    ApplicationsState.currentEntityTableData.value = [
      ...ApplicationsState.currentEntityTableData.peek().map((item) => {
        if (item.id === rowDataId) {
          return {
            id: rowDataId,
            ...rowData,
          }
        }

        return item
      }),
    ]

    applicationsStateActions.updateApplicationLocalRowData()
  },
  viewApplication: async (applicationId: string) => {
    const canViewApp = checkViewAccess()

    if (!canViewApp) {
      notification.warning({
        description:
          'Lo sentimos, no cuentas con los permisos suficientes para visualizar las solicitudes',
        message: 'Revisa tus permisos',
        placement: 'top',
      })

      return false
    }

    applicationsStateActions.setApplicationToViewMode()

    if (!FormState.showForm.value) {
      await applicationsStateActions.openApplicationView(applicationId)
    }

    return !FormState.showForm.value
  },
  // Método desacoplado para validar con RPA
  validateWithRPA: async (formValues: TApplication) => {
    // Extraer cédulas
    const cedulas: string[] = [];
    if (formValues?.documento && typeof formValues.documento === 'string') {
      cedulas.push(formValues.documento.trim());
    }
    const postulante = formValues?.CPostulante;
    if (postulante && typeof postulante === 'object' && 'documento' in postulante && typeof postulante.documento === 'string') {
      cedulas.push(postulante.documento.trim());
    }
    if (Array.isArray(formValues?.CNucleoFamiliar)) {
      formValues.CNucleoFamiliar.forEach((member: any) => {
        if (member?.docNumber && typeof member.docNumber === 'string') {
          cedulas.push(member.docNumber.trim());
        }
      });
    }
    const uniqueCedulas = Array.from(new Set(cedulas.filter(c => c.length > 0)));
    if (uniqueCedulas.length === 0) return { habilitado: true };
    // Consultar RPA
    const rpaResult = await consultarRPA(uniqueCedulas);
    // Analizar resultado
    let inhabilitado = false;
    let detalleValidacion = rpaResult.detalle;
    if (typeof detalleValidacion === 'string' && detalleValidacion.trim().startsWith('{')) {
      try {
        const parsedDetalle = JSON.parse(detalleValidacion);
        if (parsedDetalle?.results && parsedDetalle.results.length === 1) {
          const systems = parsedDetalle.results[0]?.results || {};
          Object.entries(systems).forEach(([system, sysResult]: [string, any]) => {
            if (sysResult?.status === 'success') {
              if (Array.isArray(sysResult.data) && sysResult.data.length > 0) {
                inhabilitado = true;
              }
              if (system === 'mivivienda' && sysResult.tiene_inmuebles === true) {
                inhabilitado = true;
              }
            }
          });
        }
      } catch (e) {}
    }
    return { habilitado: !inhabilitado, detalle: detalleValidacion, rpaResult };
  },

  // Método desacoplado para guardar/actualizar
  saveOrUpdateApplication: async (formValues: TApplication) => {
    const isOnline = OfflineState.isOnline.peek();
    let id = formValues.id || ApplicationsState.currentApplication.peek()?.id;
    if (!id) {
      notification.error({
        message: 'No se encontró un ID válido para la postulación',
        description: 'No se puede guardar sin un identificador único. Verifique la carga del formulario.',
        placement: 'top',
      });
      return;
    }
    const currentEntityFiles = ApplicationsState.currentEntityFilesData.peek();
    const newEntityData: TApplication = { ...formValues, ...currentEntityFiles, id };
    if (isOnline) {
      await applicationRequests.saveApplicationData(newEntityData);
      await applicationsStateActions.getApplicationStatus();
    }
    setCurrentEntityDataOnSave(newEntityData);
  },

  // Método orquestador: primero valida con RPA, luego guarda si es habilitado
  processApplication: async (formValues: TApplication) => {
    const rpaValidation = await applicationsStateActions.validateWithRPA(formValues);
    if (rpaValidation.habilitado) {
      await applicationsStateActions.saveOrUpdateApplication(formValues);
      notification.success({
        message: 'Guardado exitoso',
        description: 'La postulación se ha guardado correctamente.',
        placement: 'top',
      });
    } else {
      // Mostrar modal bloqueante de inhabilitación
      Modal.error({
        title: 'Postulación inhabilitada',
        content: (
          <div>
            <p>No se puede guardar porque se encontraron propiedades en los sistemas externos.</p>
           {/* <pre style={{whiteSpace: 'pre-wrap'}}>{rpaValidation.detalle}</pre> */}
          </div>
        ),
        okText: 'Aceptar',
        centered: true,
        maskClosable: false,
        onOk: async () => {
          const applicationId = ApplicationsState.currentApplication.value?.id;
          console.debug('[DEBUG][deleteApplication] Modal inhabilitado, id:', applicationId);
          if (applicationId) {
            try {
              await applicationsStateActions.deleteApplication(String(applicationId));
              console.debug('[DEBUG][deleteApplication] Eliminación completada');
            } catch (error) {
              console.error('[ERROR][deleteApplication] Falló la eliminación:', error);
            }
          }
          await applicationsStateActions.goToHome();
        },
      });
    }
  },
}
